import { axisBottom, axisTop, type NumberValue, type ScaleLinear, scaleLinear, select } from "d3";
import { useCallback, useEffect, useRef } from "react";
import { formatNumberByThousands } from "widgets/utils";

import { type Range } from "../../graphql.js";
import { type IBoxModel } from "./interfaces.js";

enum HorizontalAxisType {
  BOTTOM,
  TOP,
}

export interface IHorizontalAxisProps {
  boxModel: IBoxModel;
  domain: Range; // the domain space range
  tickCount: number; // approximate number of ticks
  type: HorizontalAxisType;
}

/**
 * Axis with ticks and labels below.
 *
 * created on 11/24/2016
 */
function HorizontalAxis({ boxModel, domain, tickCount, type }: IHorizontalAxisProps) {
  const axisRef = useRef<SVGGElement>(null);

  const calculateAxis = useCallback(() => {
    const width = boxModel.width ?? 0;
    const paddingLeft = boxModel.padding?.left ?? 0;
    const paddingRight = boxModel.padding?.right ?? 0;
    const contentWidth = width - paddingLeft - paddingRight;

    const scale: ScaleLinear<number, number> = scaleLinear()
      .domain([domain.start - 1, domain.stop])
      .range([0, contentWidth]);

    const formatTick = (d: NumberValue): string => {
      const value = +d; // Convert NumberValue to number
      return value === 0 ? "" : formatNumberByThousands(value) ?? "";
    };

    if (type === HorizontalAxisType.BOTTOM) {
      return axisBottom(scale)
        .ticks(tickCount)
        .tickFormat(formatTick);
    } else {
      return axisTop(scale)
        .ticks(tickCount)
        .tickFormat(formatTick);
    }
  }, [boxModel, domain, tickCount, type]);

  const renderAxis = useCallback(() => {
    if (axisRef.current) {
      const axis = calculateAxis();
      select(axisRef.current).call(axis);
    }
  }, [calculateAxis]);

  useEffect(() => {
    renderAxis();
  }, [renderAxis]);

  const paddingLeft = boxModel.padding?.left ?? 0;
  const paddingTop = boxModel.padding?.top ?? 0;
  const translate = `translate(${paddingLeft},${paddingTop - 1})`;
  return <g ref={axisRef} className="axis" transform={translate} />;
}

export { HorizontalAxisType };
export default HorizontalAxis;
